/**
 * Gallery category helpers.
 *
 * Gallery categories used to be a hardcoded list (`decks`, `kitchens`, ...).
 * They are now derived from `config.homepage.services` so the CMS, the public
 * gallery index, and each service sub-page all speak the same language.
 *
 * The category KEY stored on a gallery item equals the service slug (derived
 * from the service's href — see `hrefToServiceSlug`). There is also one
 * static "other" bucket for photos that don't belong to a specific service.
 *
 * Legacy data (saved before this refactor) used short plural keys. We migrate
 * those on read via `LEGACY_CATEGORY_MAP` in `normalizeSiteConfig`, so admins
 * don't have to re-tag anything.
 */

import type { HomepageServiceCard } from "./siteConfig";
import { hrefToServiceSlug } from "./serviceSlugs";

/** Static catch-all category — shown on /gallery/, never on a service page. */
export const OTHER_CATEGORY = "other" as const;
export const OTHER_CATEGORY_LABEL = "Other" as const;

/**
 * Mapping from pre-refactor category strings to the new service-slug-based
 * keys. Applied once on load by `normalizeSiteConfig` — idempotent (any key
 * already in new-form or unrecognized is left untouched).
 */
export const LEGACY_CATEGORY_MAP: Record<string, string> = {
  decks: "deck-installation",
  kitchens: "kitchen-renovations",
  bathrooms: "bathroom-remodeling",
  basements: "basement-renovation",
  additions: "home-additions",
  // "other" was already a valid key — no migration needed.
};

export function migrateLegacyCategory(raw: string): string {
  if (!raw) return OTHER_CATEGORY;
  return LEGACY_CATEGORY_MAP[raw] ?? raw;
}

export interface GalleryCategoryOption {
  /** The value stored on a gallery item (service slug, or `"other"`). */
  value: string;
  /** Human-readable label shown in dropdowns and badges. */
  label: string;
}

/**
 * Build the list of category options for the CMS dropdown and the public
 * gallery page, in order: services (in the admin-defined order) then Other.
 *
 * Services with blank slugs (empty href) are skipped. Duplicate slugs (admin
 * pointed two cards at the same href) are de-duped — first one wins.
 */
export function getGalleryCategoryOptions(
  services: readonly HomepageServiceCard[],
): GalleryCategoryOption[] {
  const seen = new Set<string>();
  const options: GalleryCategoryOption[] = [];

  for (const service of services) {
    const value = hrefToServiceSlug(service.href);
    if (!value || seen.has(value)) continue;
    seen.add(value);
    options.push({ value, label: service.title });
  }

  options.push({ value: OTHER_CATEGORY, label: OTHER_CATEGORY_LABEL });
  return options;
}

/**
 * Get a display label for a category key, preferring the matching service
 * title. Falls back to a prettified version of the slug when the service has
 * been removed from the CMS since the photo was tagged — so badges never go
 * blank, and orphan categories stay legible.
 */
export function getGalleryCategoryLabel(
  categoryValue: string,
  services: readonly HomepageServiceCard[],
): string {
  if (!categoryValue) return OTHER_CATEGORY_LABEL;
  if (categoryValue === OTHER_CATEGORY) return OTHER_CATEGORY_LABEL;

  const match = services.find((s) => hrefToServiceSlug(s.href) === categoryValue);
  if (match) return match.title;

  // Orphan fallback: "deck-installation" → "Deck Installation"
  return categoryValue
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}
